import React, { useState } from 'react';

/**
 * CSV export button. Builds a CSV from the currently-loaded rows on the client
 * (no server round-trip) and triggers a download, with busy/done/fail feedback.
 *
 * Two modes:
 *   • rows      — export already-loaded data synchronously (e.g. daily table).
 *   • fetchRows — async () => rows; fetch ALL filtered rows on demand before
 *                 building the CSV (e.g. the paginated most-active ranking).
 *
 * Props:
 *   columns   [{ key, label }]
 *   rows      [object]              (used when fetchRows is absent)
 *   fetchRows async () => [object]  (overrides rows when present)
 *   filename  string
 *   disabled  boolean
 */
const escapeCell = (value) => {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const buildCsv = (columns, rows) => {
  const header = columns.map((c) => escapeCell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => escapeCell(r[c.key])).join(',')).join('\n');
  return body ? `${header}\n${body}` : header;
};

const download = (csv, filename) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const LABELS = { idle: '⬇ Export CSV', busy: 'Exporting…', done: '✓ Exported', error: 'Export failed' };

const ExportButton = ({ columns, rows = [], fetchRows = null, filename = 'export.csv', disabled = false }) => {
  const [state, setState] = useState('idle');
  const empty = !fetchRows && rows.length === 0;

  const handleClick = async () => {
    if (disabled || empty || state === 'busy') return;
    setState('busy');
    try {
      const data = fetchRows ? await fetchRows() : rows;
      if (!data || !data.length) { setState('idle'); return; }
      download(buildCsv(columns, data), filename);
      setState('done');
    } catch {
      setState('error');
    }
    setTimeout(() => setState('idle'), 2000);
  };

  return (
    <button
      type="button"
      className="au-btn au-btn--ghost au-export"
      onClick={handleClick}
      disabled={disabled || empty}
      data-testid="au-export"
      aria-label="Export table to CSV"
    >
      {LABELS[state]}
    </button>
  );
};

export default ExportButton;
